import React, { useState } from 'react';
import { DatePicker } from '@roger/ui-components';
import { Pencil } from 'lucide-react';
import {
  formatDateToISO,
  formatDateToMMDDYYYY,
  parseISOToLocalDate,
  calculateAge,
} from '../../../utils/dateUtils';

// Inline Editable Date Field Component (for header)
export const InlineEditableDateField: React.FC<{
  label: string;
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  onBlur: (value: string | null) => void;
  showAge?: boolean;
  yearRange?: { fromYear: number; toYear: number };
}> = ({ label, value, onChange, onBlur, showAge, yearRange }) => {
  const [isEditing, setIsEditing] = useState(false);
  const dateValue = value ? parseISOToLocalDate(value) : undefined;

  const handleDateChange = (date: Date | undefined) => {
    const isoDate = date ? formatDateToISO(date) : null;
    onChange(isoDate);
    setIsEditing(false);
    onBlur(isoDate);
  };

  const displayValue = value
    ? `${formatDateToMMDDYYYY(value)}${showAge ? ` (${calculateAge(value)} yrs)` : ''}`
    : 'N/A';

  if (isEditing) {
    return (
      <span className='inline-flex items-center'>
        <span className='text-muted-foreground'>{label}:</span>{' '}
        <DatePicker
          date={dateValue}
          onDateChange={handleDateChange}
          placeholder='Select date'
          datePickerSize='sm'
          className='w-32 ml-1'
          yearRange={yearRange}
        />
      </span>
    );
  }

  return (
    <span
      className='inline-flex items-center cursor-pointer hover:bg-blue-50 hover:text-blue-700 rounded px-1 -mx-1 transition-colors group'
      onClick={() => setIsEditing(true)}
      title={`Click to edit ${label.toLowerCase()}`}
    >
      <span className='text-muted-foreground group-hover:text-blue-600'>
        {label}:
      </span>{' '}
      <span className='group-hover:underline'>{displayValue}</span>
      <Pencil className='h-3 w-3 ml-1 hidden group-hover:block text-blue-500' />
    </span>
  );
};
